import { GetStaticPropsContext } from "next";
import { gql, useQuery } from "@apollo/client";
import * as MENUS from "@/constants/menus";
import { BlogInfoFragment, MenuItemFragment } from "@/fragments";
import { Container, Main } from "@/components/atoms";
import { ErrorHeader, Footer } from "@/components/organisms";
import { getNextStaticProps } from "@faustwp/core";
import { SEO, PreviewLoader } from "@/components/molecules";
import { useTranslate } from "@/hooks/useTranslate";
import { ERROR_500_BUTTON } from "@/i18n/translations/ERROR_500_BUTTON";
import { ERROR_500_SUBHEADING } from "@/i18n/translations/ERROR_500_SUBHEADING";
import NewMenuItemFragment from "@/fragments/NewMenuItem";
import DynamicHeader from "@/components/organisms/DynamicHeader";
import { Locale } from "@/types";

export default function Page({ locale }: { locale: string }) {
  const { data, loading } = useQuery(Page.query, {
    variables: { ...Page.variables({ locale }), locale },
  });
  const { translate } = useTranslate();

  if (loading) {
    return <PreviewLoader />;
  }

  const siteTitle = data?.generalSettings?.title;
  const siteDescription = data?.generalSettings?.description;
  const primaryMenu = data?.headerMenuItems?.nodes ?? [];
  const newMenuItems = data?.newMenuItems?.nodes ?? [];
  const footerMenu = data?.footerMenuItems?.nodes ?? [];

  return (
    <>
      <SEO title={siteTitle} description={siteDescription} />
      <DynamicHeader
        title={siteTitle}
        description={siteDescription}
        menuItems={primaryMenu}
        enhancedMenuItems={newMenuItems}
      />
      <Main>
        <Container>
          <ErrorHeader
            className="text-center text-slate-700"
            heading="500!"
            subHeading={translate(ERROR_500_SUBHEADING)}
            buttonText={translate(ERROR_500_BUTTON)}
            buttonLink={`${locale}/`}
          />
        </Container>
      </Main>
      <Footer title={siteTitle} menuItems={footerMenu} />
    </>
  );
}

Page.query = gql`
  ${BlogInfoFragment}
  ${MenuItemFragment}
  ${NewMenuItemFragment}
  query GetPageData(
    $headerLocation: MenuLocationEnum
    $footerLocation: MenuLocationEnum
    $locale: LanguageCodeFilterEnum = EN
    $newMenuLocation: MenuLocationEnum
  ) {
    generalSettings {
      ...BlogInfoFragment
    }
    headerMenuItems: menuItems(
      where: { location: $headerLocation, language: $locale }
      first: ${MENUS.NUMBER_OF_MENU_ITEMS}
    ) {
      nodes {
        ...MenuItemFragment
      }
    }
    footerMenuItems: menuItems(
      where: { location: $footerLocation, language: $locale }
      first: ${MENUS.NUMBER_OF_MENU_ITEMS}
    ) {
      nodes {
        ...MenuItemFragment
      }
    }
    newMenuItems: menuItems(
      where: { location: $newMenuLocation, language: $locale }
      first: ${MENUS.NUMBER_OF_MENU_ITEMS}
    ) {
      nodes {
        ...NewMenuItemFragment
      }
    }
  }
`;

Page.variables = (({ locale }: { locale: string }) => {
  return {
    headerLocation: MENUS.newHeaderLocation(locale as Locale),
    footerLocation: MENUS.newFooterLocation(locale as Locale),
    newMenuLocation: MENUS.newMenuLocation(locale as Locale),
  };
}) as any;

export function getStaticProps(context: GetStaticPropsContext) {
  return getNextStaticProps(context, {
    Page,
  });
}
